// Per-condition empathetic content for landing pages.
// Category-level approach text is shared to keep this file lean.

export const CATEGORY_APPROACH = {
  "Pregnancy & maternity":
    "In every trimester my role is the same — to listen carefully, watch the details that matter and explain the tests and choices in plain language. You will always know what is normal, what is being monitored and why. When something needs attention we will act promptly and gently, together.",
  "Fertility & reproductive health":
    "We begin with your story — how long, what you have tried, what you have been told. A focused work-up follows, and only the tests that actually change the plan. From there the path is shared: what to try first, what to consider next, what to do if it is not working — always at your pace.",
  "Menstrual & hormonal":
    "Periods can tell us a lot when we ask the right questions. I take time to understand your cycle, your symptoms and how it is affecting your day-to-day. Investigations are done only where they help, and treatment is chosen with you — lifestyle and reassurance first, medication only when it truly earns its place.",
  "Gynaecological conditions":
    "The plan begins with a careful examination and the right scans — nothing more, nothing less. Most conditions have several reasonable ways forward and I will walk you through each, honestly. Surgery is only ever suggested when it is genuinely the best option, and always as one choice among several.",
  "Menopause & midlife":
    "This is a transition, not an illness — but the symptoms can be very real. We look at the whole picture: sleep, mood, bones, heart, intimacy. HRT is one option; there are several others. We choose together, review honestly and adjust as your body settles.",
  "Adolescent & preventive":
    "This is a judgement-free space. Whether it is a first period, a routine check-up or a difficult question, I speak straight and without hurry. Parents are welcome or you can consult privately — whatever feels right.",
  "Aesthetic gynaecology":
    "Aesthetic gynaecology is personal. We speak openly, look at what is realistic and what genuinely helps — nothing is ever pushed. Every procedure is fully explained: what it does, what it does not, and every alternative.",
};

// Each entry: intro (empathy hook) + body (2–3 lines about the condition).
export const CONDITIONS_MAP = {
  // Pregnancy & maternity
  "antenatal-prenatal-care": {
    name: "Antenatal & prenatal care",
    category: "Pregnancy & maternity",
    intro: "I know how much rides on this first pregnancy — the joy, the worry, all the questions nobody warned you about.",
    body: "Every trimester brings a new set of tests, symptoms and decisions. My clinic keeps that steady for you: regular check-ins, honest answers, and the confidence that someone is watching the details so you can enjoy the journey.",
  },
  "high-risk-pregnancy": {
    name: "High-risk pregnancy",
    category: "Pregnancy & maternity",
    intro: "I understand what a high-risk label does to a mother — the fear, the second-guessing, the hyper-alertness.",
    body: "With 25 years in obstetrics I have walked many women through complicated pregnancies safely. Closer monitoring, clear plans for the ‘what-ifs’, and a calm hand at every scan — that is what changes the experience.",
  },
  "gestational-diabetes": {
    name: "Gestational diabetes",
    category: "Pregnancy & maternity",
    intro: "I know the panic that follows a positive GTT — the diet worries, the finger-prick anxiety, the ‘will my baby be okay’.",
    body: "Most gestational diabetes is well managed with structured meals and gentle activity — medication is added only if the numbers ask for it. We keep it practical, cultural and doable, right up to a safe delivery.",
  },
  "pre-eclampsia-pregnancy-hypertension": {
    name: "Pre-eclampsia & pregnancy hypertension",
    category: "Pregnancy & maternity",
    intro: "I know how frightening a rising blood pressure reading can feel in pregnancy.",
    body: "Early detection is everything. Close monitoring, the right medication when needed and clear thresholds for hospital care keep mother and baby safe — often right through to term.",
  },
  "anaemia-in-pregnancy": {
    name: "Anaemia in pregnancy",
    category: "Pregnancy & maternity",
    intro: "I see many mothers-to-be running on empty and putting it down to ‘just tiredness’.",
    body: "Anaemia in pregnancy is common in India and very treatable. A proper work-up, food-first advice and the right supplement — oral or IV — restores your energy and protects your baby.",
  },
  "recurrent-miscarriage": {
    name: "Recurrent miscarriage",
    category: "Pregnancy & maternity",
    intro: "I know how quietly heavy pregnancy loss can be — especially when it has happened more than once.",
    body: "A careful work-up looks for hormonal, structural, immune and genetic factors — many of which have real answers. We plan the next pregnancy with confidence, closer support and a monitoring pattern that suits you.",
  },
  "twin-multiple-pregnancy": {
    name: "Twin & multiple pregnancy",
    category: "Pregnancy & maternity",
    intro: "Two heartbeats bring double the joy and, honestly, twice the questions.",
    body: "Multiple pregnancies need a closer eye — more scans, tailored nutrition, and a delivery plan chosen well in advance. We keep it structured so nothing important is left to the last moment.",
  },
  "postpartum-care": {
    name: "Postpartum care",
    category: "Pregnancy & maternity",
    intro: "The weeks after delivery can feel like nobody is asking about you anymore.",
    body: "Postpartum care matters as much as the pregnancy itself — healing, breastfeeding, mood, contraception, getting your body back on your terms. I keep this window unhurried and honest.",
  },
  "postnatal-depression-support": {
    name: "Postnatal depression support",
    category: "Pregnancy & maternity",
    intro: "I know the guilt that comes when you ‘should be happy’ but you feel nothing like it.",
    body: "Postnatal depression is a medical condition, not a character flaw. A safe conversation, a proper assessment and a stepped plan — including gentle referral where helpful — makes a real difference.",
  },

  // Fertility & reproductive health
  "pcos-pcod": {
    name: "PCOS / PCOD",
    category: "Fertility & reproductive health",
    intro: "I know what women navigate with PCOS — the unpredictable periods, the skin, the weight that won’t shift, the anxious months of trying.",
    body: "PCOS is not one problem, so it does not have one plan. In my practice we start with your story, then a targeted work-up. From there — lifestyle first, medication only where it earns its place, fertility support when it matters.",
  },
  "female-infertility": {
    name: "Female infertility",
    category: "Fertility & reproductive health",
    intro: "I have sat across many women asking the same, hopeful question — ‘Is it me?’",
    body: "Infertility is rarely a single answer. A structured, unhurried work-up covers ovulation, tubes, uterus and hormones — and we move step by step, not straight to IVF.",
  },
  "male-fertility-work-up": {
    name: "Male fertility work-up",
    category: "Fertility & reproductive health",
    intro: "In almost half of couples the male factor plays a role — investigating it is not blame, it is basic thoroughness.",
    body: "A semen analysis, a focused history and, where needed, hormonal or urology inputs give clarity fast. It saves months of guessing and shapes the couple’s plan honestly.",
  },
  "ovulation-disorders": {
    name: "Ovulation disorders",
    category: "Fertility & reproductive health",
    intro: "If your cycle feels like a mystery every month, you are not alone.",
    body: "Ovulation is easy to check and often easy to correct. Simple tracking, targeted tests and — where indicated — gentle stimulation can bring back a predictable, fertile cycle.",
  },
  endometriosis: {
    name: "Endometriosis",
    category: "Fertility & reproductive health",
    intro: "I know that ‘bad period pain’ often hides years of endometriosis that no one took seriously.",
    body: "Diagnosis begins with really listening, then imaging and, where needed, laparoscopy. Management is layered — pain control, hormonal options and surgery, chosen for your goals, not a textbook.",
  },
  "recurrent-pregnancy-loss": {
    name: "Recurrent pregnancy loss",
    category: "Fertility & reproductive health",
    intro: "Every loss is its own quiet grief. Repeated losses deserve real answers, not platitudes.",
    body: "A structured work-up looks at genetics, uterus, hormones, clotting and immunity. Many causes have specific, evidence-based treatments — and better monitoring in the next pregnancy improves outcomes.",
  },
  "pre-conception-counselling": {
    name: "Pre-conception counselling",
    category: "Fertility & reproductive health",
    intro: "The best pregnancies often start three months before you conceive.",
    body: "One thoughtful visit covers folic acid, vaccinations, weight, thyroid, sugars, chronic medicines and family history — so your body is genuinely ready when the time comes.",
  },
  "iui-ivf-guidance": {
    name: "IUI / IVF guidance",
    category: "Fertility & reproductive health",
    intro: "IUI and IVF are big decisions — emotionally, physically and financially.",
    body: "I help you understand exactly what each protocol involves, what the honest odds look like and when the right time is. If assisted reproduction is the next step, we plan it with a trusted centre and stay involved throughout.",
  },
  "fibroids-affecting-fertility": {
    name: "Fibroids affecting fertility",
    category: "Fertility & reproductive health",
    intro: "Not every fibroid needs to come out — but some genuinely stand in the way of a pregnancy.",
    body: "We look at location, size and symptoms carefully. Only the fibroids that truly matter get treated, and where surgery helps, minimally invasive options preserve fertility.",
  },

  // Menstrual & hormonal
  "irregular-periods": {
    name: "Irregular periods",
    category: "Menstrual & hormonal",
    intro: "Periods that don’t follow a rhythm can quietly rule your calendar.",
    body: "Behind an irregular cycle is usually a specific, treatable reason — from PCOS to thyroid to lifestyle. A short work-up finds it, and a tailored plan brings back predictability.",
  },
  "heavy-or-prolonged-bleeding": {
    name: "Heavy or prolonged bleeding",
    category: "Menstrual & hormonal",
    intro: "I have seen women silently plan their week around a period. That is not something you should have to live with.",
    body: "Heavy bleeding has real causes — hormonal, structural, thyroid — and real solutions, most of them non-surgical. We stop the bleed, treat the anaemia, and address the cause.",
  },
  "painful-periods-dysmenorrhoea": {
    name: "Painful periods (dysmenorrhoea)",
    category: "Menstrual & hormonal",
    intro: "Painful periods have been normalised for far too long.",
    body: "Severe cramps often point to endometriosis, adenomyosis or fibroids. A gentle work-up and layered treatment plan get you back to a life that is not paused every month.",
  },
  "absent-periods-amenorrhoea": {
    name: "Absent periods (amenorrhoea)",
    category: "Menstrual & hormonal",
    intro: "When periods stop turning up, it usually means your body is asking for something.",
    body: "Stress, weight changes, thyroid, prolactin, PCOS, perimenopause — the causes range widely and each has a clear next step. We investigate calmly and treat the root.",
  },
  "pms-pmdd": {
    name: "PMS & PMDD",
    category: "Menstrual & hormonal",
    intro: "The week before your period should not feel like a completely different person moved in.",
    body: "PMS and PMDD are real, hormonal and treatable. Lifestyle, targeted supplements and — where needed — hormonal or SSRI options can genuinely soften those days.",
  },
  "hormonal-imbalance": {
    name: "Hormonal imbalance",
    category: "Menstrual & hormonal",
    intro: "‘Hormonal imbalance’ is a phrase used far too loosely — but the feeling behind it is real.",
    body: "We look for what is actually off — thyroid, prolactin, androgens, insulin, cortisol — and treat only what genuinely needs treating. No shotgun panels, no hype.",
  },
  "thyroid-reproduction": {
    name: "Thyroid & reproduction",
    category: "Menstrual & hormonal",
    intro: "The thyroid quietly runs periods, fertility and pregnancy.",
    body: "Small imbalances can slow conception and affect early pregnancy. We keep the numbers in the right range at each stage — before, during and after.",
  },
  "hirsutism-unwanted-hair": {
    name: "Hirsutism / unwanted hair",
    category: "Menstrual & hormonal",
    intro: "Unwanted hair carries an emotional weight most clinicians overlook.",
    body: "The underlying reason usually sits in PCOS, insulin resistance or a specific hormonal source — worth investigating properly. Treatment blends medical, lifestyle and cosmetic options, chosen for the long game.",
  },

  // Gynaecological
  "uterine-fibroids": {
    name: "Uterine fibroids",
    category: "Gynaecological conditions",
    intro: "Many fibroids are quiet neighbours; a few are the reason for heavy periods, pain or trouble conceiving.",
    body: "We differentiate the ones that matter with a careful scan and clinical picture. Most are watched, some are treated medically, and only a minority need surgery — laparoscopically wherever possible.",
  },
  "ovarian-cysts": {
    name: "Ovarian cysts",
    category: "Gynaecological conditions",
    intro: "An incidental ovarian cyst on a scan can feel alarming — usually, it is not.",
    body: "Most cysts are functional and resolve on their own. We characterise them properly, monitor thoughtfully and only intervene when it is genuinely the right call.",
  },
  "endometrial-polyps": {
    name: "Endometrial polyps",
    category: "Gynaecological conditions",
    intro: "Small polyps inside the uterus can cause irregular bleeding, spotting or fertility issues.",
    body: "A hysteroscopic day-care removal is quick, elegant and often definitive. We choose it only when the polyp is truly the cause of your symptoms.",
  },
  adenomyosis: {
    name: "Adenomyosis",
    category: "Gynaecological conditions",
    intro: "The heavy, painful periods that ‘nothing helps’ often have adenomyosis behind them.",
    body: "Diagnosis is clinical plus scan. A layered plan — hormonal options, IUS, targeted medication and, only if truly needed, surgery — gets women their life back.",
  },
  "pelvic-inflammatory-disease-pid": {
    name: "Pelvic inflammatory disease (PID)",
    category: "Gynaecological conditions",
    intro: "PID is under-diagnosed and often silent — but its long-term effect on fertility is real.",
    body: "Prompt, complete treatment now protects your tubes and future pregnancies. We treat you and, where relevant, your partner, and follow up until it is truly resolved.",
  },
  "recurrent-vaginal-infections": {
    name: "Recurrent vaginal infections",
    category: "Gynaecological conditions",
    intro: "Infections that keep coming back are exhausting — and rarely just bad luck.",
    body: "We identify the specific organism, look at contributing factors (hormones, hygiene products, sugars) and treat both partners where indicated. Prevention is baked into the plan.",
  },
  "urinary-tract-infections": {
    name: "Urinary tract infections",
    category: "Gynaecological conditions",
    intro: "Frequent UTIs are more common in women than most doctors acknowledge.",
    body: "We treat the current episode properly, investigate for structural or hormonal contributors, and set up a prevention plan — often without long-term antibiotics.",
  },
  "abnormal-pap-smear": {
    name: "Abnormal Pap smear",
    category: "Gynaecological conditions",
    intro: "An abnormal Pap does not usually mean cancer — but it does deserve a clear next step.",
    body: "Colposcopy and, where indicated, HPV testing or biopsy give you a real answer, quickly. Follow-up is structured so nothing quietly gets missed.",
  },
  "cervical-vulval-health": {
    name: "Cervical & vulval health",
    category: "Gynaecological conditions",
    intro: "The vulva and cervix rarely get the attention they deserve in routine care.",
    body: "From itching and lichen sclerosus to precancerous changes, an unhurried examination and the right investigations give you a proper plan and lasting relief.",
  },

  // Menopause & midlife
  perimenopause: {
    name: "Perimenopause",
    category: "Menopause & midlife",
    intro: "The years before menopause can quietly rewrite how you feel — sleep, mood, cycles, energy.",
    body: "This is one of the most under-treated phases of a woman’s life. A proper assessment and thoughtful — often non-hormonal first — options give you back the version of you that got lost somewhere.",
  },
  "menopause-management": {
    name: "Menopause management",
    category: "Menopause & midlife",
    intro: "Menopause is a chapter, not a decline.",
    body: "We look at symptoms, bones, heart, sleep and intimacy together — and choose from the full toolkit: lifestyle, non-hormonal medication and modern HRT where it genuinely fits.",
  },
  "hot-flashes-night-sweats": {
    name: "Hot flashes & night sweats",
    category: "Menopause & midlife",
    intro: "Waking up drenched, feeling ‘on fire’ mid-conversation — it is exhausting.",
    body: "Several safe options — lifestyle, non-hormonal, and modern HRT — genuinely reduce vasomotor symptoms. We pick the one that fits your health profile and preference.",
  },
  "vaginal-dryness": {
    name: "Vaginal dryness",
    category: "Menopause & midlife",
    intro: "Vaginal dryness is common, treatable, and never something to be embarrassed about.",
    body: "Local oestrogen, moisturisers, laser therapy and lifestyle steps can restore comfort and intimacy — quietly and effectively.",
  },
  "hormone-replacement-therapy-hrt": {
    name: "Hormone replacement therapy (HRT)",
    category: "Menopause & midlife",
    intro: "Modern HRT is very different from the story you may have grown up hearing.",
    body: "We talk honestly about who benefits, who should be cautious and how we prescribe it today — reviewed regularly and adjusted as your body settles.",
  },
  "bone-health-osteoporosis": {
    name: "Bone health & osteoporosis",
    category: "Menopause & midlife",
    intro: "Bones lose density quietly after menopause — until a small fall becomes a big fracture.",
    body: "A DEXA scan, sensible calcium and vitamin D, resistance activity and — where indicated — medication protects the next thirty years.",
  },
  "midlife-weight-mood": {
    name: "Midlife weight & mood",
    category: "Menopause & midlife",
    intro: "The midlife weight shift and mood dip are not about willpower.",
    body: "Hormones, sleep, thyroid and stress all conspire. We assess the whole picture and make small, doable changes that actually last.",
  },

  // Adolescent & preventive
  "first-period-concerns": {
    name: "First period concerns",
    category: "Adolescent & preventive",
    intro: "The first period brings a lot of questions — for the girl and for her mother.",
    body: "We keep the conversation simple, kind and evidence-based. What is normal, what is not, and when to ask again.",
  },
  "adolescent-gynaecology": {
    name: "Adolescent gynaecology",
    category: "Adolescent & preventive",
    intro: "Teenage bodies deserve a doctor who really listens, without judgement.",
    body: "From cycle irregularities to acne, weight or intimate health, we give young women space to ask anything — with or without a parent in the room.",
  },
  "contraception-counselling": {
    name: "Contraception counselling",
    category: "Adolescent & preventive",
    intro: "The right contraception is deeply personal.",
    body: "We look at your health, plans and preferences honestly — pills, IUDs, implants, natural methods — and choose what fits you now, with room to change later.",
  },
  "cervical-cancer-screening": {
    name: "Cervical cancer screening",
    category: "Adolescent & preventive",
    intro: "Cervical cancer is one of the most preventable cancers we know of.",
    body: "A Pap smear and HPV test at the right intervals catches changes years before they matter. Screening is quick, gentle and hugely reassuring.",
  },
  "hpv-vaccination": {
    name: "HPV vaccination",
    category: "Adolescent & preventive",
    intro: "The HPV vaccine is one of the most important gifts you can give a girl — or yourself.",
    body: "We discuss timing, doses and safety in plain language, without pressure. It works best early, but has clear benefits well into adulthood.",
  },
  "breast-health-check": {
    name: "Breast health check",
    category: "Adolescent & preventive",
    intro: "Every woman should know her breasts — and have someone qualified check them regularly.",
    body: "A clinical exam, guidance on self-checks and age-appropriate imaging catches things early and calms anxiety.",
  },
  "well-woman-check-ups": {
    name: "Well-woman check-ups",
    category: "Adolescent & preventive",
    intro: "One thoughtful visit a year prevents most of the problems women come to me in a panic about.",
    body: "Blood pressure, sugars, thyroid, vitamin D, Pap, breast exam, contraception and life stage — reviewed together, not as a checklist.",
  },

  // Aesthetic gynaecology
  "vaginal-rejuvenation": {
    name: "Vaginal rejuvenation",
    category: "Aesthetic gynaecology",
    intro: "Vaginal changes after childbirth or with age are common — and often bothering more women than say so.",
    body: "We discuss what genuinely helps — Kegels and pelvic floor work first, then laser or surgical options if they truly fit. Realistic outcomes, always.",
  },
  "laser-gynaecology": {
    name: "Laser gynaecology",
    category: "Aesthetic gynaecology",
    intro: "Laser gynaecology can help with dryness, mild laxity and certain kinds of stress incontinence.",
    body: "It is not for everyone. We assess honestly, explain what a course of treatment can and cannot do, and never promise more than the evidence supports.",
  },
  "post-delivery-care": {
    name: "Post-delivery care",
    category: "Aesthetic gynaecology",
    intro: "Your body deserves proper post-delivery attention — beyond a six-week check.",
    body: "Scars, pelvic floor, intimate comfort, hair and skin — we look at the whole picture and support your recovery, in your time.",
  },
  "intimate-wellness-consultation": {
    name: "Intimate wellness consultation",
    category: "Aesthetic gynaecology",
    intro: "Intimate wellness is a real conversation — not a taboo, not a marketing pitch.",
    body: "One private, unhurried consultation covers what is bothering you, what is normal, and what actually helps. No pressure, no judgement.",
  },
};

export function slugify(name) {
  return name
    .toLowerCase()
    .replace(/[^\w]+/g, "-")
    .replace(/^-|-$/g, "");
}
